/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import {
  AvailableAlerts,
  FetchAlertProperties,
  FilterAlertInterFace,
  RequestAlertInterFace,
  AlertInitialProperties,
} from '../../interface/alerts.interface';
// import device from '../../utils/api/device';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import notificationApi from '../../utils/api/notification';

// a thunk to fetch devices
export const fetchAllAlerts = createAsyncThunk<FetchAlertProperties, RequestAlertInterFace>(
  'alert-management/fetchAllAlerts',
  async (data: RequestAlertInterFace, { dispatch }) => {
    try {
      const response = await notificationApi.fetchAlerts(removeEmptyKeys(data));
      return {
        ...response.data,
        results: response.data.results.map((alertData: AvailableAlerts) => ({
          ...alertData,
          date: ConvertUTCtoDate(alertData.createdAt),
          time: FormatUtcString(ConvertUTCtoHHMM(alertData.createdAt)),
          id: alertData._id,
        })),
      };
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// Initial state
const initialState: AlertInitialProperties = {
  alerts: [],
  loading: false,
  page: DefaultApiLimit.page,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  filter: {},
};

// Create the slice
export const alertSlice = createSlice({
  name: 'alert-management',
  initialState,
  reducers: {
    /**
     * Change the device point status function
     * @param state
     * @param action
     */
    setAlertFilterData: (
      state: AlertInitialProperties,
      action: PayloadAction<FilterAlertInterFace>,
    ) => {
      state.filter = action.payload;
    },
    /**
     * Function that reset slice to initial state
     * @param state
     */
    resetAlertToInitialState: (state) => {
      Object.assign(state, initialState);
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<AlertInitialProperties>) => {
    builder
      .addCase(fetchAllAlerts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllAlerts.fulfilled, (state, action: PayloadAction<FetchAlertProperties>) => {
        state.loading = false;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.alerts = action.payload.results;
      })
      .addCase(fetchAllAlerts.rejected, (state) => {
        state.loading = false;
      });
  },
});

// Export the actions
export const { setAlertFilterData, resetAlertToInitialState } = alertSlice.actions;

// Export the reducer
export default alertSlice.reducer;
